import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import momentZ from 'moment-timezone';
import MDSpinner from 'react-md-spinner';
import {List} from 'react-window';
import {spinnerColor} from '../../utils/StyleUtils';
import {loadWebUsers} from '../../actions/users';
import {
  rowSurfaces,
  WEB_USER_PRESENCE_LABELS,
  WEB_USER_SURFACE_LABELS,
  WebUserRow,
  WebUsersCursor,
} from '../../models/WebUser';

interface Props {
    users: WebUserRow[];
    isLoading: boolean;
    hasMore: boolean;
    nextCursor: WebUsersCursor | null;
    loadWebUsers: (cursor?: WebUsersCursor | null) => void;
}

// Mirrors .user-item height in _users.scss.
const ROW_HEIGHT = 36;

const formatDate = (value: string | null) => (value ? momentZ(value).format('YYYY-MM-DD HH:mm') : '—');

/** Badge tooltip: how many logins on this surface and when the first and last one happened. */
const surfaceTitle = (surface: string, usage: {firstSeenAt: string, lastSeenAt: string, count: number}) =>
  `${surface}: ${usage.count} login(s), ${formatDate(usage.firstSeenAt)} → ${formatDate(usage.lastSeenAt)}`;

const Row = ({index, style, users}: {index: number, style: React.CSSProperties, users: WebUserRow[]}) => {
  const user = users[index];
  return (
    <div className={'user-item'} style={style}>
      <div className={'users-email-column'} title={user.email ?? ''}>{user.email ?? '—'}</div>
      <div className={'users-date-column'}>{formatDate(user.createdAt)}</div>
      <div className={'users-status-column'}>
        {/* Class per status; statuses the CSS doesn't know yet fall back to the base badge. */}
        <span className={`users-badge users-badge-${user.subscriptionStatus}`}>
          {user.subscriptionStatus}
        </span>
      </div>
      <div className={'users-status-column'}>
        <span className={`users-badge users-source-${(user.source ?? '').toLowerCase()}`}>
          {(user.source ?? '—').toLowerCase()}
        </span>
      </div>
      {/* Where this login has been used. Empty for rows that predate surface tracking — the
          absence of a badge is not a claim that the surface was never used. */}
      <div className={'users-surfaces-column'}>
        {rowSurfaces(user).length === 0
          ? <span className={'users-surfaces-empty'}>—</span>
          : rowSurfaces(user).map((surface) => (
            <span
              key={surface}
              className={`users-badge users-surface-${surface}`}
              title={surfaceTitle(surface, user.surfaces![surface])}
            >
              {WEB_USER_SURFACE_LABELS[surface]}
            </span>
          ))}
      </div>
      <div className={'users-presence-column'}>
        <span className={`users-badge users-presence-${user.presence ?? 'unknown'}`}>
          {WEB_USER_PRESENCE_LABELS[user.presence] ?? 'unknown'}
        </span>
      </div>
      <div className={'users-language-column'}>{user.language ?? '—'}</div>
      <div className={'users-date-column'}>{formatDate(user.statusChangedAt)}</div>
    </div>
  );
};

const UsersTable = ({users, isLoading, hasMore, nextCursor, loadWebUsers}: Props) => {
  // The first page is requested by the section wrapper, not here: React runs child effects before
  // the parent's, and in the Astify panel the socket is opened by the wrapper — a request fired
  // from this component would leave before any connection existed and be dropped silently.
  //
  // Keep pulling pages until the backend reports the end. The list is virtualized, so the cost of
  // holding every loaded row is memory only — the DOM keeps just the visible window.
  useEffect(() => {
    if (hasMore && !isLoading && nextCursor) {
      loadWebUsers(nextCursor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoading, nextCursor]);

  // Full-height spinner for the first page, matching MetricsTabel and the tracks list.
  if (isLoading && users.length === 0) {
    return (
      <div className='spinner-container' style={{marginTop: 'auto', marginBottom: 'auto', width: '100%'}}>
        <MDSpinner size={100} singleColor={spinnerColor}/>
      </div>
    );
  }

  if (users.length === 0) {
    return <div className={'users-empty'}>No users for the selected period</div>;
  }

  return (
    <div className={'reels-users-tabel'}>
      <div className={'header-tabel'}>
        <div className={'users-email-column header-right-border'}>Email</div>
        <div className={'users-date-column header-right-border'}>Created</div>
        <div className={'users-status-column header-right-border'}>Status</div>
        <div className={'users-status-column header-right-border'}>Source</div>
        <div className={'users-surfaces-column header-right-border'}>Used in</div>
        <div className={'users-presence-column header-right-border'}>Presence</div>
        <div className={'users-language-column header-right-border'}>Language</div>
        <div className={'users-date-column'}>Status changed</div>
      </div>

      {/* The generic is explicit: react-window supplies index and style itself, so inferring the
          row props from the component would demand them in rowProps as well. */}
      <List<{users: WebUserRow[]}>
        rowComponent={Row}
        rowCount={users.length}
        rowHeight={ROW_HEIGHT}
        rowProps={{users}}
        style={{height: 'calc(100vh - 220px)', width: '100%'}}
      />

      {/* Subsequent pages: a small inline spinner under the table, so the rows already loaded stay
          visible and readable while the rest arrives. */}
      {isLoading && (
        <div className={'users-loading-more'}>
          <MDSpinner size={20} singleColor={spinnerColor}/>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  users: state.users.users,
  isLoading: state.users.isLoading,
  hasMore: state.users.hasMore,
  nextCursor: state.users.nextCursor,
});

const mapDispatchToProps = (dispatch: any) => ({
  loadWebUsers: bindActionCreators(loadWebUsers, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(UsersTable);
